import type { AuthDecision } from '../engine/auth'
import type { Party } from '../engine/events'
import { formatMoney } from '../engine/money'
import type { Settlement } from '../engine/types'
import { DECIDED_BY_LABEL, STEP_LABEL, type Depth } from './describe'

/**
 * A frame is a complete snapshot of what the diagram and the decision panel
 * should show at one step. Precomputing them is what makes stepping forwards,
 * stepping backwards and playing all the same operation.
 */
export interface Frame {
  marker: { at: Party; text: string; variant: 'message' | 'money' }
  activeEdge?: [Party, Party]
  lit: Party[]
  deciding?: Party
  down?: Party
  caption: string
  clockMs?: number
  schemeShown: number
  issuerShown: number
}

/** The authorisation round trip, one frame per hop and one per issuer check. */
export function authFrames(decision: AuthDecision, amountMinor: number, depth: Depth): Frame[] {
  const frames: Frame[] = []
  const requestText =
    depth === 2 ? '0100' : depth === 1 ? 'auth request' : `asking for ${formatMoney(amountMinor)}`

  const push = (f: Frame) => frames.push(f)

  push({
    marker: { at: 'merchant', text: requestText, variant: 'message' },
    activeEdge: ['cardholder', 'merchant'],
    lit: ['cardholder', 'merchant'],
    caption:
      depth === 0
        ? `You present the card for ${formatMoney(amountMinor)}. The shop does not know yet whether it will be paid.`
        : `Card presented for ${formatMoney(amountMinor)}. The terminal builds the authorisation request.`,
    clockMs: 0,
    schemeShown: 0,
    issuerShown: 0,
  })

  push({
    marker: { at: 'acquirer', text: requestText, variant: 'message' },
    activeEdge: ['merchant', 'acquirer'],
    lit: ['merchant', 'acquirer'],
    caption:
      depth === 0
        ? "The shop sends the request to its own bank."
        : 'Acquirer receives the request and routes it to VisaNet by the account number range.',
    clockMs: 190,
    schemeShown: 0,
    issuerShown: 0,
  })

  push({
    marker: { at: 'scheme', text: requestText, variant: 'message' },
    activeEdge: ['acquirer', 'scheme'],
    lit: ['acquirer', 'scheme'],
    caption:
      depth === 0
        ? 'Visa receives it. The two banks never speak to each other directly.'
        : 'VisaNet receives the message. The acquirer and issuer have no direct link.',
    clockMs: 380,
    schemeShown: 0,
    issuerShown: 0,
  })

  // Whatever VisaNet does before, or instead of, asking the issuer.
  decision.schemeChecks.forEach((check, i) => {
    const isStandIn = check.stage === 'stip'
    push({
      marker: { at: 'scheme', text: depth === 2 ? check.stage.toUpperCase() : 'checking', variant: 'message' },
      lit: ['scheme'],
      deciding: 'scheme',
      ...(isStandIn && !check.passed && check.stage === 'stip' ? { down: 'issuer' as Party } : {}),
      caption: depth === 0 ? check.plain.detail : check.detail,
      clockMs: 470 + i * 90,
      schemeShown: i + 1,
      issuerShown: 0,
    })
  })

  const schemeShown = decision.schemeChecks.length
  const lastSchemeClock = 470 + schemeShown * 90

  if (!decision.issuerBypassed) {
    push({
      marker: { at: 'issuer', text: requestText, variant: 'message' },
      activeEdge: ['scheme', 'issuer'],
      lit: ['scheme', 'issuer'],
      caption:
        depth === 0
          ? 'Your bank receives the request and starts checking.'
          : 'VisaNet switches the 0100 to the issuer host.',
      clockMs: lastSchemeClock + 100,
      schemeShown,
      issuerShown: 0,
    })

    decision.issuerChecks.forEach((check, i) => {
      push({
        marker: {
          at: 'issuer',
          text: depth === 2 ? check.step : 'checking',
          variant: 'message',
        },
        lit: ['issuer'],
        deciding: 'issuer',
        caption: `${STEP_LABEL[check.step]}: ${check.detail}`,
        clockMs: lastSchemeClock + 180 + i * 110,
        schemeShown,
        issuerShown: i + 1,
      })
    })
  }

  const issuerShown = decision.issuerChecks.length
  const responseText =
    depth === 2
      ? `0110 ${decision.responseCode}`
      : depth === 1
        ? `response ${decision.responseCode}`
        : decision.outcome === 'declined'
          ? 'refused'
          : 'yes'

  const turnaround = decision.issuerBypassed ? lastSchemeClock + 120 : lastSchemeClock + 200 + issuerShown * 110

  // The return leg. It starts wherever the decision was actually made.
  if (!decision.issuerBypassed) {
    push({
      marker: { at: 'scheme', text: responseText, variant: 'message' },
      activeEdge: ['issuer', 'scheme'],
      lit: ['issuer', 'scheme'],
      caption:
        depth === 0
          ? 'Your bank sends its answer back.'
          : `Issuer returns ${decision.responseCode}. ${DECIDED_BY_LABEL[decision.decidedBy]}.`,
      clockMs: turnaround,
      schemeShown,
      issuerShown,
    })
  }

  push({
    marker: { at: 'acquirer', text: responseText, variant: 'message' },
    activeEdge: ['scheme', 'acquirer'],
    lit: ['scheme', 'acquirer'],
    ...(decision.issuerBypassed && decision.decidedBy !== 'scheme-routing'
      ? { down: 'issuer' as Party, deciding: 'scheme' as Party }
      : {}),
    caption: decision.issuerBypassed
      ? depth === 0
        ? 'Visa answers on your bank behalf. Your bank still knows nothing about this.'
        : `${DECIDED_BY_LABEL[decision.decidedBy]}. The issuer host was never involved.`
      : 'VisaNet switches the 0110 response back to the acquirer.',
    clockMs: turnaround + 180,
    schemeShown,
    issuerShown,
  })

  push({
    marker: { at: 'merchant', text: responseText, variant: 'message' },
    activeEdge: ['acquirer', 'merchant'],
    lit: ['acquirer', 'merchant'],
    caption: 'Acquirer passes the answer to the terminal.',
    clockMs: turnaround + 350,
    schemeShown,
    issuerShown,
  })

  push({
    marker: {
      at: 'cardholder',
      text: decision.outcome === 'declined' ? 'declined' : 'approved',
      variant: 'message',
    },
    activeEdge: ['cardholder', 'merchant'],
    lit: ['cardholder', 'merchant'],
    caption:
      decision.outcome === 'declined'
        ? 'Receipt prints. Nothing was set aside, and no money moved.'
        : depth === 0
          ? 'Receipt prints. The money is set aside, and it still has not moved anywhere.'
          : 'Approved. A hold now sits against available balance. Nothing has cleared or settled.',
    clockMs: turnaround + 500,
    schemeShown,
    issuerShown,
  })

  return frames
}

/**
 * Settlement runs the other way round the diagram, and it is money rather than
 * a message. Note that it never touches the cardholder.
 */
export function settlementFrames(settlements: Settlement[], depth: Depth): Frame[] {
  const gross = settlements.reduce((s, x) => s + x.grossMinor, 0)
  const issuerPays = settlements.reduce((s, x) => s + x.issuerPaysMinor, 0)
  const schemePays = settlements.reduce((s, x) => s + x.schemePaysMinor, 0)
  const net = settlements.reduce((s, x) => s + x.merchantNetMinor, 0)
  const interchange = settlements.reduce((s, x) => s + x.interchangeMinor, 0)
  const schemeFee = settlements.reduce((s, x) => s + x.schemeFeeMinor, 0)

  const money = (at: Party, amount: number) => ({
    at,
    text: formatMoney(amount),
    variant: 'money' as const,
  })

  return [
    {
      marker: money('issuer', issuerPays),
      activeEdge: ['issuer', 'scheme'],
      lit: ['issuer'],
      caption:
        depth === 0
          ? `Your bank took ${formatMoney(gross)} from you, and keeps ${formatMoney(interchange)} of it as its fee.`
          : `Issuer settles ${formatMoney(issuerPays)}, retaining ${formatMoney(interchange)} as the interchange reimbursement fee out of ${formatMoney(gross)}.`,
      schemeShown: 0,
      issuerShown: 0,
    },
    {
      marker: money('scheme', schemePays),
      activeEdge: ['issuer', 'scheme'],
      lit: ['issuer', 'scheme'],
      deciding: 'scheme',
      caption:
        depth === 0
          ? `Visa passes the money on, keeping ${formatMoney(schemeFee)} for itself.`
          : `Visa nets the position between the two banks through the Visa Settlement Service and takes ${formatMoney(schemeFee)} in fees.`,
      schemeShown: 0,
      issuerShown: 0,
    },
    {
      marker: money('acquirer', schemePays),
      activeEdge: ['scheme', 'acquirer'],
      lit: ['scheme', 'acquirer'],
      caption:
        depth === 0
          ? "The shop's bank receives the money."
          : 'Acquirer receives settlement from Visa.',
      schemeShown: 0,
      issuerShown: 0,
    },
    {
      marker: money('merchant', net),
      activeEdge: ['acquirer', 'merchant'],
      lit: ['acquirer', 'merchant'],
      caption:
        depth === 0
          ? `The shop finally has the money: ${formatMoney(net)} of the ${formatMoney(gross)} you paid. This is the first moment anyone actually has your money.`
          : `Merchant funded ${formatMoney(net)} against ${formatMoney(gross)} authorised. The difference is the merchant service charge.`,
      schemeShown: 0,
      issuerShown: 0,
    },
  ]
}
